import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import './Blog.css';

const API = process.env.REACT_APP_API_URL || 'https://api.stratosstandard.com';

const COLORS = ['#0d2045', '#1a3a5c', '#1e1b4b', '#064e3b', '#78350f', '#1e293b'];

export default function Blog() {
  const [activeTag, setActiveTag] = useState('All');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/posts`);
        const data = await res.json();
        if (!cancelled) setPosts(Array.isArray(data.posts) ? data.posts : []);
      } catch (_) {
        if (!cancelled) setPosts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Tags are derived from the published posts so the filter bar always reflects
  // what's actually live, with the editor-flagged "featured" post floated first.
  const TAGS = ['All', ...Array.from(new Set(posts.map(p => p.tag).filter(Boolean)))];

  const ordered = [...posts].sort((a, b) => (b.featured === true) - (a.featured === true));
  const filtered = activeTag === 'All' ? ordered : ordered.filter(p => p.tag === activeTag);
  const [featured, ...rest] = filtered;

  return (
    <>
      <Seo
        title="Blog — Celebrating Early Achievement"
        description="Stories and ideas about recognizing and celebrating what young children can already do, plus tips for warm, play-based verification sessions."
        path="/blog"
      />
      <section className="page-hero">
        <div className="container">
          <p className="eyebrow" style={{ color: '#3ba3e8', position: 'relative' }}>Latest Insights</p>
          <h1 className="page-hero-title">Blog &amp; Updates</h1>
          <p className="page-hero-sub">Expert insights on early childhood development, our AERO philosophy, and celebrating your child's achievements.</p>
        </div>
      </section>

      <section className="section-pad">
        <div className="container">
          {/* Tags filter */}
          <div className="tag-filter">
            {TAGS.map(tag => (
              <button
                key={tag}
                className={`tag-btn ${activeTag === tag ? 'active' : ''}`}
                onClick={() => setActiveTag(tag)}
              >
                {tag}
              </button>
            ))}
          </div>

          {/* Featured post */}
          {featured && (
            <Link to={`/blog/${featured.slug}`} className="featured-post">
              <div className="featured-img" style={{ background: COLORS[0] }}>
                <div className="featured-img-inner" aria-hidden="true">
                  <span className="featured-tag-overlay">{featured.tag}</span>
                </div>
              </div>
              <div className="featured-body">
                <div className="blog-meta">
                  <span className="badge badge-sky">{featured.tag}</span>
                  {featured.featured && <span className="badge badge-gold">Featured</span>}
                  <span className="blog-date">{featured.date}</span>
                  <span className="blog-date">· {featured.readTime}</span>
                </div>
                <h2 className="featured-title">{featured.title}</h2>
                <p className="featured-excerpt">{featured.excerpt}</p>
                <span className="btn btn-aero btn-sm" style={{ marginTop: '20px', display: 'inline-flex' }}>
                  Read Article →
                </span>
              </div>
            </Link>
          )}

          {/* Rest grid */}
          {rest.length > 0 && (
            <div className="blog-grid" style={{ marginTop: '40px' }}>
              {rest.map((post, i) => (
                <Link to={`/blog/${post.slug}`} key={post.slug} className="blog-card">
                  <div className="blog-img-placeholder">
                    <div className="blog-img-bg" style={{ background: COLORS[(i + 1) % COLORS.length] }} />
                  </div>
                  <div className="blog-card-body">
                    <div className="blog-meta">
                      <span className="badge badge-sky">{post.tag}</span>
                      <span className="blog-date">{post.date}</span>
                    </div>
                    <h3 className="blog-title">{post.title}</h3>
                    <p className="blog-excerpt">{post.excerpt}</p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                      <span className="blog-read-more">Read more →</span>
                      <span className="blog-date">{post.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {loading && (
            <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
              <p>Loading articles…</p>
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
              <p>No posts in this category yet. Check back soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter */}
      <section className="newsletter-section">
        <div className="container newsletter-inner">
          <div>
            <p className="eyebrow" style={{ color: '#3ba3e8' }}>Stay in the Loop</p>
            <h2>Get Early Childhood Insights Delivered to Your Inbox</h2>
            <p>Join 1,000+ parents receiving our monthly newsletter on development milestones, tips, and Stratos Standard news.</p>
          </div>
          <form className="newsletter-form" onSubmit={e => e.preventDefault()}>
            <input type="email" className="form-input" placeholder="your@email.com" required />
            <button type="submit" className="btn btn-primary">Subscribe</button>
          </form>
        </div>
      </section>
    </>
  );
}
